/**
 * Every item menu's actions: ids, order, words and icons. One registry for
 * the workspace right-click menu, the collection's top-bar ⋮ and the detail
 * pages' ⋮ (`DetailActionMenu`), so an action sits in the same place, with
 * the same word and icon, wherever it is offered.
 */

export type ItemMenuActionId =
  // Open
  | 'open'
  | 'openInNewTab'
  | 'viewRuns'
  // Share
  | 'copyLink'
  // Organize
  | 'rename'
  | 'editLink'
  | 'move'
  | 'copy'
  | 'favorite'
  // Transform
  | 'convertToNote'
  | 'convertToProtocol'
  | 'convertToSpreadsheet'
  | 'draftProtocol'
  // History
  | 'versionHistory'
  | 'viewTranscript'
  | 'approveVersion'
  | 'newVersion'
  // Export
  | 'export'
  | 'download'
  // Trash
  | 'restore'
  // Danger
  | 'deleteVersion'
  | 'delete'
  | 'deleteForever';

/** One choice inside an entry's in-place submenu (Export ▸ PDF). */
export interface ItemMenuSubEntry {
  /** Stable within its entry; sent with the action as `option`. */
  readonly id: string;
  readonly label: string;
  readonly icon: string;
  readonly onSelect: () => void;
}

interface ItemMenuEntryBase {
  readonly id: ItemMenuActionId;
  /** Omitted = enabled. A disabled entry stays visible and does nothing. */
  readonly enabled?: boolean;
  /** Why it is disabled, and how to fix it (from `resolveSelectionActions`). */
  readonly reason?: string;
  /** Replaces the registry's word, for a label that carries state ("Delete version 3"). */
  readonly label?: string;
}

/** An entry does one thing, or expands in place into its choices (Export ▸ PDF). */
export type ItemMenuEntry = ItemMenuEntryBase &
  (
    | { readonly onSelect: () => void; readonly children?: undefined }
    | { readonly children: ReadonlyArray<ItemMenuSubEntry>; readonly onSelect?: undefined }
  );

/** Slots a caller fills with its own element (a submenu, Manage access). */
export type ItemMenuSlotId = 'add' | 'access';

/**
 * Order and grouping, by intent: Open, Create, Share, Organize, Transform,
 * History, Export, Trash, Danger. A divider sits between non-empty groups,
 * and a caller's footer (`ArtifactMenuMetadata`) follows the last one.
 */
export const ITEM_MENU_GROUPS: ReadonlyArray<ReadonlyArray<ItemMenuActionId | ItemMenuSlotId>> = [
  ['open', 'openInNewTab', 'viewRuns'],
  ['add'],
  ['copyLink', 'access'],
  ['rename', 'editLink', 'move', 'copy', 'favorite'],
  ['convertToNote', 'convertToProtocol', 'convertToSpreadsheet', 'draftProtocol'],
  ['versionHistory', 'viewTranscript', 'approveVersion', 'newVersion'],
  ['export', 'download'],
  // A trashed item's two actions (Trash on a phone).
  ['restore'],
  ['deleteVersion', 'delete', 'deleteForever'],
];

/** Drawn and worded as destructive in every menu. */
export const DANGER_ACTIONS: ReadonlySet<ItemMenuActionId> = new Set([
  'deleteVersion',
  'delete',
  'deleteForever',
]);

export const ITEM_MENU_LABELS: Record<ItemMenuActionId, string> = {
  open: 'Open',
  openInNewTab: 'Open in new tab',
  viewRuns: 'View runs',
  copyLink: 'Copy link',
  rename: 'Rename',
  editLink: 'Edit link',
  move: 'Move to...',
  copy: 'Copy to...',
  favorite: 'Add to favorites',
  convertToNote: 'Convert to note',
  convertToProtocol: 'Convert to protocol',
  convertToSpreadsheet: 'Convert to spreadsheet',
  draftProtocol: 'Draft protocol from video',
  versionHistory: 'Version history',
  viewTranscript: 'View original transcript',
  approveVersion: 'Approve this version',
  newVersion: 'New version',
  export: 'Export',
  download: 'Download',
  restore: 'Restore',
  deleteVersion: 'Delete version',
  delete: 'Delete',
  deleteForever: 'Delete forever',
};

export const ITEM_MENU_ICONS: Record<ItemMenuActionId, string> = {
  open: 'open_in_new',
  openInNewTab: 'tab',
  viewRuns: 'sensors',
  copyLink: 'link',
  rename: 'edit',
  editLink: 'link',
  move: 'drive_file_move',
  copy: 'content_copy',
  favorite: 'star',
  convertToNote: 'article',
  convertToProtocol: 'science',
  convertToSpreadsheet: 'table_chart',
  draftProtocol: 'science',
  versionHistory: 'history',
  viewTranscript: 'subtitles',
  approveVersion: 'lock',
  newVersion: 'edit_document',
  export: 'download',
  download: 'download',
  restore: 'restore_from_trash',
  deleteVersion: 'layers_clear',
  delete: 'delete',
  deleteForever: 'delete_forever',
};
